// CalendarDay > CalendarDay
import React from 'react'
import UxGrid from '../UxGrid'
import Scale from '../ScaleData'
import { format } from '../utils/Dates'
import { convertFrom24To12 } from '../utils/Time'
import { padZero } from '../utils/String'
import { CalendarDayProps, defaultProps } from './ICalendarDay'

export default class CalendarDay<T extends object> extends React.PureComponent<CalendarDayProps<T>> {
	static readonly defaultProps = defaultProps
	private readonly _increment = 1
	private readonly _minBlockLength = this.props.minBlockLength
	private readonly _blocksPerHour = 60 / this._minBlockLength
	private readonly _scale = new Scale(0, 24 * this._blocksPerHour, this._increment)

	private _ixOutsideOfFullScale = (ix: number) => {
		return ix > this._scale.totalIncrements || ix < 0
	}

	private _labelConversion = (ix: number) => {
		const { use24 } = this.props

		// need to make sure we have correct positive equivalent since negative ixs are a thing in infinite scroll
		const limitedIx = ix % this._scale.totalIncrements
		const hourlyIx = limitedIx / this._blocksPerHour
		const absolute = ix > 0 ? hourlyIx : 24 + hourlyIx

		let hour = absolute % 24
		let min = 0
		let period = ''

		if (!use24) {
			const timeObj = convertFrom24To12(hour)

			hour = timeObj.hour
			min = timeObj.minute
			period = timeObj.period
		}

		const time = !use24
			? min > 0 ? `${hour}:${padZero(min)}${period.substring(0, 1)}` : `${hour}${period.substring(0, 1)}`
			: `${hour}:${padZero(min)}`

		return time
	}

	private _getColumnName = (scrollIx: number) => {
		const date = new Date(this.props.date.getTime())

		if (this._ixOutsideOfFullScale(scrollIx)) {
			const dayDiff = Math.floor(scrollIx / this._scale.totalIncrements)

			date.setDate(date.getDate() + dayDiff)
		}

		return format(date, 'en-us', {
			weekday: 'short',
			month: 'short',
			day: 'numeric'
		})
	}

	render() {
		const { use24, locale, date, minBlockLength, ...rest } = this.props

		return (
			<UxGrid<T>
				autoActivate={true}
				scale={this._scale}
				columns={[this._getColumnName]}
				multiTypeSelect={true}
				labelConversion={this._labelConversion}
				{...rest}
			/>
		)
	}
}
